import type {
  ProductLookupIdType,
  ProductLookupRow,
} from '~/features/deliveries/types/delivery-runs.types';

import type { Product } from '../types/catalog-operations.types';

/**
 * Which id list + `idType` to send the delivery lookup for a page of curation
 * products.
 *
 * UNVERIFIED AGAINST A LIVE RESPONSE — BE PR #627 (`deliveries/{connectionId}/
 * products/lookup`) is not yet merged, so this was built against the contract
 * doc only. The contract's own field reference describes the audit row's
 * `sourceProductId` as "the upstream enrichment row id — same meaning on every
 * connector", which per `docs/content-enrichment-product-listing.md` is the
 * curation `feed_product_id` — the same id already used as
 * `PipelineEnrichedProduct.productId`. That equivalence is the assumption
 * `idType: 'source'` + `product.id` below encodes.
 *
 * If a real batch disagrees, the fix is local: change `idType` to `'auto'` and
 * send `product.sourceProductId` instead (note: that is the CURATION wire's
 * `source_product_id` — the retailer/offer id — a different field with the
 * same name on a different service; do not conflate the two). The join in
 * {@link indexLookupRowsByCurationId} is written defensively against exactly
 * this uncertainty — see its own doc comment.
 */
export function buildLookupIdPlan(products: readonly Pick<Product, 'id'>[]): {
  idType: ProductLookupIdType;
  ids: string[];
} {
  return { idType: 'source', ids: products.map((p) => p.id) };
}

/**
 * Indexes lookup rows by every id field that might carry our curation
 * `feed_product_id` (`sourceProductId`, `productId`, `externalId`), so the join
 * back to a `Product` succeeds regardless of which field the live contract
 * actually populates with it — see {@link buildLookupIdPlan} for why this is
 * defensive rather than a single confident key. A row is indexed under every
 * non-empty candidate it carries; the last write for a shared key wins, which
 * only matters if two distinct products' ids collide across fields (not
 * expected in practice).
 */
export function indexLookupRowsByCurationId(
  rows: readonly ProductLookupRow[],
): Map<string, ProductLookupRow> {
  const map = new Map<string, ProductLookupRow>();
  for (const row of rows) {
    for (const candidate of [row.sourceProductId, row.productId, row.externalId]) {
      if (typeof candidate === 'string' && candidate.trim() !== '') {
        map.set(candidate, row);
      }
    }
  }
  return map;
}

/** Looks up a product's delivery audit row from the indexed map. */
export function matchLookupRow(
  product: Pick<Product, 'id'>,
  rowsById: Map<string, ProductLookupRow>,
): ProductLookupRow | undefined {
  return rowsById.get(product.id);
}
